import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertTriangle, Info } from "lucide-react";

type Kind = "success" | "info" | "warn";
type Toast = { id: number; kind: Kind; text: string };

const ToastCtx = createContext<(t: Omit<Toast, "id">) => void>(() => {});
// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, "id">) => {
    setItems((arr) => [...arr, { ...t, id: Date.now() + Math.random() }]);
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-[60] flex w-full max-w-md -translate-x-1/2 flex-col gap-2 px-4">
          {items.map((t) => (
            <ToastItem
              key={t.id}
              toast={t}
              onDone={() => setItems((arr) => arr.filter((x) => x.id !== t.id))}
            />
          ))}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  );
}

function ToastItem({ toast, onDone }: { toast: Toast; onDone: () => void }) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    const t = setTimeout(() => {
      setEntered(false);
      setTimeout(onDone, 200);
    }, 2600);
    return () => {
      cancelAnimationFrame(r);
      clearTimeout(t);
    };
  }, [onDone]);

  const Icon = toast.kind === "success" ? CheckCircle2 : toast.kind === "warn" ? AlertTriangle : Info;
  const color =
    toast.kind === "success"
      ? "text-[var(--color-success)]"
      : toast.kind === "warn"
      ? "text-[var(--color-warning)]"
      : "text-[var(--color-brand-2)]";

  return (
    <div
      className={
        "pointer-events-auto flex items-center gap-2.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 shadow-[0_12px_30px_-12px_rgba(15,23,42,0.25)] transition-all duration-200 " +
        (entered ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0")
      }
      role="status"
    >
      <Icon size={16} className={color} />
      <span className="text-[13px] text-[var(--color-fg)]">{toast.text}</span>
    </div>
  );
}
